import { createRef } from 'react';
import ResourceModal from 'app/components/ResourceModal';
import ResourceModalPanel from 'app/components/ResourceModalPanel';
import UploadModal from 'app/components/Uploads/UploadModal';
import useMomentReplaceUploadForm from 'app/hooks/useMomentReplaceUploadForm';
import MomentForm from './MomentForm';

import {
  updateMoment,
  getMomentUploadUrl,
  createMomentFile,
  destroyMoment
} from 'app/services/moments'

import {
  uploadFile
} from 'shared/services/FileService';

import { MOMENT_DOWNLOAD_URL } from 'shared/utils/Constants';

import {
  getLocalizedDate,
  getLocalizedTime,
  getDay,
  formatBytes
} from 'shared/utils/Utils';

const MomentPanel = props => {
  const {
    moment,
    action,
    onClose,
    onUploadComplete,
    onDelete
  } = props;

  const {
    formIsOpen,
    onAdd,
    onFormClose,
    onFormSubmit
  } = useMomentReplaceUploadForm({
    onUploadComplete,
    moment
  });

  // if we're creating a moment then no actions panel will be shown
  if (action === 'create') {
    return null;
  }

  const replaceMomentField = createRef();

  let download = (filename, momentId) => {
    let link = document.createElement('a');

    link.href = MOMENT_DOWNLOAD_URL(momentId);
    link.download = filename;
    link.click();
  }

  const downloadFile = () => {
    download(moment.filename, moment.id);
  }

  const downloadOriginal = () => {
    download(moment.filename, moment.orig_moment_id);
  }

  let formatTime = timestamp => {
    if (!timestamp) {
      return null;
    }

    let day = getDay(timestamp, 'short');
    let date = getLocalizedDate('en-us', timestamp, 'short');
    let time = getLocalizedTime('en-us', timestamp, 'short');
    let divStyle = { margin: '0.5rem 0' };
    let pStyle = { margin: '0' };

    return (
      <div style={divStyle}>
        <p style={pStyle}>{day} {date}</p>
        <p style={pStyle}>{time}</p>
      </div>
      );
    }

  let downloadActions = [
    { label: 'Download File', callback: downloadFile }
  ];

  if (moment.digital_edit) {
    downloadActions.push({ label: 'Download Original', callback: downloadOriginal })
  }

  let cards = [
    {
      label: 'Image',
      image: {
        id: moment.id,
        src: moment.preview_url,
        w: moment.width,
        h: moment.height,
        lastUpdate: moment.uploaded,
        original: moment.print_url,
        is_video: moment.type && moment.type.includes("video")
      },
      details: [
        { label: 'Date', value: formatTime(moment.date) },
        { label: 'Width', value: moment.width },
        { label: 'Height', value: moment.height }
      ],
      actions: downloadActions
    },
    {
      label: 'File',
      details: [
        { label: 'Uploaded', value: formatTime(moment.uploaded) },
        { label: 'Type', value: moment.type },
        { label: 'Size', value: formatBytes(moment.size) }
      ],
      actions: [
        { label: 'Upload New', callback: onAdd }
      ]
    },
    {
      label: 'Delete Moment',
      message: 'Deleting a moment will permanently remove it. It will no longer appear on customer stories.',
      actions: [
        { label: 'Delete Moment', callback: onDelete }
      ]
    }
  ]

  return (
    <div>
      <ResourceModalPanel cards={cards} />
      <UploadModal
        isOpen={formIsOpen}
        onClose={onFormClose}
	onFormSubmit={onFormSubmit}
      />
    </div>
  );
}

const MomentModal = props => {
  const {
    isOpen,
    onClose,
    onDestroy,
    moment,
    onUploadComplete
  } = props;

  const action = moment ? 'update' : 'create';
  const onDelete = () => onDestroy(moment.id);

  let title, subTitle = null;
  if (action === 'update') {
    title = `Moment ${moment.id}`;
    subTitle = moment.filename;
  } else {
    title = 'New Moment';
  }

  // currently there is no "New Moment" form,
  // nor is there any field that the user can really edit.
  // ideally the ability to tag the moment to other
  // jobs and customers here would be great.
  // disabling the form until something like this is needed
  let form = null;
  let formDisabled = true;
  if (!formDisabled) {
    form = <MomentForm {...{ moment, action, onClose }} />;
  }

  return (
    <ResourceModal {...{ isOpen, onClose, title, subTitle }}>
      <MomentPanel {...{ moment, action, onClose, onUploadComplete, onDelete }} />
      {form}
    </ResourceModal>
  );
}

export default MomentModal;
